import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, PackageOpen, Plus, Search, X } from 'lucide-react';
import { ErrorReportButton } from '@/components/ErrorReportPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { inlineError } from '@/lib/errorReport';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import { BILLING_PERIOD_LABELS, type BillingPeriod, type ProductListItem } from '@/types/product';
import { entryTotal, selectionTotal, type PickedProduct } from './productPickerSelection';
import { ALL_PERIODS, useProductPicker } from './useProductPicker';

const money = (cents: number | null) =>
  cents === null ? 'Op aanvraag' : formatCurrency(cents / 100);
const range = (product: ProductListItem) =>
  !product.priceRange
    ? product.onRequestText || 'Op aanvraag'
    : product.priceRange.minCents === product.priceRange.maxCents
      ? money(product.priceRange.minCents)
      : `${money(product.priceRange.minCents)} – ${money(product.priceRange.maxCents)}`;

interface ProductPickerPanelProps {
  open: boolean;
  onClose: () => void;
  destinations: Array<{ tableId: string; title: string }>;
  destinationId: string;
  onDestinationChange: (id: string) => void;
  onPick: (items: PickedProduct[]) => void;
}

/** Geen modal: de offerte houdt zijn eigen scroll en de selectie blijft onderaan in beeld. */
export default function ProductPickerPanel({
  open,
  onClose,
  destinations,
  destinationId,
  onDestinationChange,
  onPick,
}: ProductPickerPanelProps) {
  const picker = useProductPicker(open);
  const [showSelection, setShowSelection] = useState(false);
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [details, setDetails] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef(new Map<string, HTMLElement>());
  const groups = useMemo(
    () =>
      picker.categories
        .map(([name]) => ({
          name,
          products: picker.filteredProducts.filter(
            (product) =>
              (product.category ?? 'Zonder categorie') === name &&
              (!showSelection || picker.selectedIds.includes(product.id))
          ),
        }))
        .filter((group) => group.products.length),
    [picker.categories, picker.filteredProducts, picker.selectedIds, showSelection]
  );
  const totals = useMemo(
    () =>
      Object.entries(BILLING_PERIOD_LABELS)
        .map(([period, label]) => {
          const entries = picker.selectedEntries.filter(
            (entry) => entry.product.billingPeriod === period
          );
          return { period, label, count: entries.length, ...selectionTotal(entries) };
        })
        .filter((total) => total.count),
    [picker.selectedEntries]
  );

  useEffect(() => {
    if (!open) {
      setShowSelection(false);
      setCollapsed([]);
      setDetails([]);
      setActiveCategory(null);
    }
  }, [open]);
  if (!open) return null;

  const jumpToCategory = (name: string | null) => {
    setShowSelection(false);
    setActiveCategory(name);
    if (name) setCollapsed((current) => current.filter((value) => value !== name));
    requestAnimationFrame(() => {
      const list = listRef.current;
      const section = name ? sectionRefs.current.get(name) : null;
      if (list)
        list.scrollTo({
          top: section ? section.offsetTop - list.offsetTop : 0,
          behavior: 'instant',
        });
    });
  };

  return (
    <section
      className="product-picker-panel"
      data-product-picker-panel
      data-investment-inspector
      aria-label="Product toevoegen"
    >
      <header className="workspace-panel-heading">
        <h2>
          Product toevoegen <span>{picker.products.length} producten</span>
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="workspace-quiet-button"
          aria-label="Productcatalogus sluiten"
        >
          Sluiten <X size={16} />
        </button>
      </header>
      <div className="catalog-search">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">Zoek producten</span>
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            autoFocus
            className="h-9 pl-9 text-xs"
            placeholder="Zoek op productnaam of label"
            value={picker.search}
            onChange={(event) => picker.setSearch(event.target.value)}
          />
        </label>
        <Select value={picker.period} onValueChange={picker.setPeriod}>
          <SelectTrigger
            aria-label="Facturatieperiode"
            className="h-9 w-36 shrink-0 bg-card text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent data-investment-inspector>
            <SelectItem value={ALL_PERIODS}>Alle periodes</SelectItem>
            {(Object.entries(BILLING_PERIOD_LABELS) as Array<[BillingPeriod, string]>).map(
              ([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
        <button
          className="workspace-quiet-button"
          type="button"
          onClick={() =>
            setCollapsed(
              collapsed.length === picker.categories.length
                ? []
                : picker.categories.map(([name]) => name)
            )
          }
        >
          {collapsed.length === picker.categories.length && collapsed.length
            ? 'Alles uitklappen'
            : 'Alles inklappen'}
        </button>
      </div>
      {picker.error && (
        <div role="alert" className="catalog-error">
          {picker.error.message} <ErrorReportButton report={picker.error.report} />{' '}
          <button type="button" onClick={picker.retryCatalog}>
            Opnieuw proberen
          </button>
        </div>
      )}
      <div className="catalog-body">
        <aside className="catalog-categories" aria-label="Productcategorieën">
          <h3>Categorieën</h3>
          <nav>
            <button
              type="button"
              aria-pressed={!activeCategory && !showSelection}
              onClick={() => jumpToCategory(null)}
            >
              <span>Alle producten</span>
              <span>{picker.products.length}</span>
            </button>
            {picker.categories.map(([name, count]) => (
              <button
                key={name}
                type="button"
                aria-pressed={activeCategory === name && !showSelection}
                onClick={() => jumpToCategory(name)}
              >
                <span>{name}</span>
                <span>{count}</span>
              </button>
            ))}
          </nav>
          <p>Klik op een categorie om ernaartoe te springen. Je selectie blijft bewaard.</p>
        </aside>
        <div
          className="catalog-list scrollbar-thin"
          ref={listRef}
          aria-label="Producten per categorie"
          tabIndex={0}
        >
          <div className="catalog-column-heading">
            <span>{showSelection ? 'Geselecteerde producten' : 'Product'}</span>
            <span>Prijs excl. btw</span>
          </div>
          {picker.catalogLoading ? (
            <div className="workspace-empty" role="status">
              <Loader2 className="animate-spin" size={24} />
              <p>Catalogus laden…</p>
            </div>
          ) : groups.length === 0 ? (
            <div className="workspace-empty">
              <PackageOpen size={28} />
              <h3>
                {showSelection ? 'Nog geen producten geselecteerd' : 'Geen producten gevonden'}
              </h3>
              <p>Pas je zoekterm of filters aan.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  picker.setSearch('');
                  picker.setPeriod(ALL_PERIODS);
                  setShowSelection(false);
                }}
              >
                Filters wissen
              </Button>
            </div>
          ) : (
            groups.map(({ name, products }) => (
              <section
                key={name}
                ref={(element) => {
                  if (element) sectionRefs.current.set(name, element);
                  else sectionRefs.current.delete(name);
                }}
              >
                <button
                  type="button"
                  className="catalog-category-heading"
                  aria-expanded={!collapsed.includes(name)}
                  onClick={() =>
                    setCollapsed((current) =>
                      current.includes(name)
                        ? current.filter((value) => value !== name)
                        : [...current, name]
                    )
                  }
                >
                  {collapsed.includes(name) ? (
                    <ChevronRight size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                  <strong>{name}</strong>
                  <span>{products.length}</span>
                </button>
                {!collapsed.includes(name) &&
                  products.map((product) => {
                    const entry = picker.entries[product.id];
                    const selected = picker.selectedIds.includes(product.id);
                    const expanded = details.includes(product.id);
                    const total = entry ? entryTotal(entry) : null;
                    return (
                      <div
                        key={product.id}
                        className={cn('catalog-product', selected && 'is-selected')}
                      >
                        <div className="catalog-product-main">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => picker.toggleProduct(product)}
                            aria-label={`${product.name} selecteren`}
                          />
                          <button
                            type="button"
                            className="catalog-product-name"
                            onClick={() => picker.chooseProduct(product)}
                          >
                            <span>{product.name}</span>
                            <small>
                              {BILLING_PERIOD_LABELS[product.billingPeriod]}
                              {(product.variantNames?.length ?? 0) > 1
                                ? ` · ${product.variantNames!.length} varianten`
                                : ''}
                            </small>
                          </button>
                          <span className="catalog-product-price">
                            {selected && entry
                              ? entry.loading
                                ? 'Prijs laden…'
                                : (total?.text ?? money(total?.cents ?? null))
                              : range(product)}
                          </span>
                          <button
                            type="button"
                            aria-label={`Details van ${product.name}`}
                            aria-expanded={expanded}
                            className="catalog-details-button"
                            onClick={() => {
                              void picker.chooseProduct(product);
                              setDetails((current) =>
                                expanded
                                  ? current.filter((id) => id !== product.id)
                                  : [...current, product.id]
                              );
                            }}
                          >
                            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        </div>
                        {selected && entry && (
                          <div className="catalog-product-options">
                            {entry.loading && !entry.snapshot ? (
                              <span role="status" className="flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" /> Variants en prijs
                                laden…
                              </span>
                            ) : (
                              <>
                                {entry.variants.length > 1 && (
                                  <Select
                                    value={entry.variantId}
                                    disabled={entry.loading}
                                    onValueChange={(value) =>
                                      void picker.changeVariant(product.id, value)
                                    }
                                  >
                                    <SelectTrigger
                                      aria-label={`Variant van ${product.name}`}
                                      className="h-[30px] w-auto min-w-28 max-w-full gap-2 bg-card text-xs text-foreground"
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent data-investment-inspector>
                                      {entry.variants.map((variant) => (
                                        <SelectItem key={variant.id} value={variant.id}>
                                          {variant.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                                <label>
                                  Aantal{' '}
                                  <input
                                    type="number"
                                    min="0.01"
                                    step="any"
                                    value={entry.quantity}
                                    onChange={(event) =>
                                      picker.patchEntry(product.id, {
                                        quantity: event.target.value,
                                      })
                                    }
                                  />
                                </label>
                                {entry.snapshot && (
                                  <span>{entry.snapshot.lines.length} rekenregels</span>
                                )}
                                {entry.quantity &&
                                  Number(entry.quantity.replace(',', '.')) <= 0 && (
                                    <span role="alert">Aantal moet groter zijn dan nul.</span>
                                  )}
                              </>
                            )}
                          </div>
                        )}
                        {expanded && selected && entry?.snapshot && (
                          <div className="catalog-product-details">
                            <label>
                              Korting (%){' '}
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="any"
                                value={entry.discount}
                                onChange={(event) =>
                                  picker.patchEntry(product.id, { discount: event.target.value })
                                }
                              />
                            </label>
                            {entry.snapshot.priceMode === 'on_request' && (
                              <label>
                                Prijs of tekst{' '}
                                <Input
                                  placeholder="Bijv. 125,00 of Op maat"
                                  value={entry.customPrice}
                                  onChange={(event) =>
                                    picker.patchEntry(product.id, {
                                      customPrice: event.target.value,
                                    })
                                  }
                                />
                              </label>
                            )}
                            {entry.snapshot.lines.map((line) => (
                              <div key={line.code} className="catalog-cost-line">
                                <span>{line.description}</span>
                                <span>{line.hours} u</span>
                                <span>{money(line.totalCents)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </section>
            ))
          )}
        </div>
      </div>
      <footer className="catalog-selection-footer">
        <div className="catalog-selection-summary">
          <div className="flex flex-wrap items-center gap-4">
            <strong aria-live="polite">
              {picker.selectedIds.length} product{picker.selectedIds.length === 1 ? '' : 'en'}{' '}
              geselecteerd
            </strong>
            <button
              type="button"
              className="workspace-link"
              onClick={() => {
                setShowSelection(!showSelection);
                setCollapsed([]);
                picker.setSearch('');
                picker.setPeriod(ALL_PERIODS);
                listRef.current?.scrollTo({ top: 0 });
              }}
            >
              {showSelection ? 'Toon alle producten' : 'Toon selectie'}
            </button>
          </div>
          <div className="catalog-selection-totals" aria-live="polite">
            {totals.length ? (
              totals.map((total) => (
                <div key={total.period}>
                  <span>
                    {total.label} · excl. btw
                    {total.openItems ? ` · ${total.openItems} op aanvraag` : ''}
                  </span>
                  <strong>{money(total.cents)}</strong>
                </div>
              ))
            ) : (
              <span>Kies producten uit de lijst.</span>
            )}
          </div>
        </div>
        <div className="catalog-add-actions">
          <label>
            Toevoegen aan{' '}
            <Select value={destinationId} onValueChange={onDestinationChange}>
              <SelectTrigger
                aria-label="Fase voor nieuwe producten"
                className="h-9 w-auto min-w-36 max-w-60 gap-2 bg-card text-xs text-foreground"
              >
                <SelectValue placeholder="Kies een fase" />
              </SelectTrigger>
              <SelectContent data-investment-inspector>
                {destinations.map((destination) => (
                  <SelectItem key={destination.tableId} value={destination.tableId}>
                    {destination.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <Button
            size="sm"
            disabled={
              !picker.canAdd || !destinations.some((entry) => entry.tableId === destinationId)
            }
            onClick={() => {
              try {
                onPick(picker.picks());
              } catch (error) {
                picker.setError(
                  inlineError(
                    error instanceof Error ? error.message : 'Producten toevoegen mislukt.',
                    error
                  )
                );
              }
            }}
          >
            <Plus size={16} />
            {picker.selectedIds.length || ''} product{picker.selectedIds.length === 1 ? '' : 'en'}{' '}
            toevoegen
          </Button>
        </div>
      </footer>
    </section>
  );
}
